import React, { useState } from 'react';
import { ConfirmationModal } from '@components/confirmation-modal';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import {
    CommunityAccessLevel,
    CommunityType,
    ChatNotificationSelection,
    LocaleCode
} from '@api/ChallengeClient';

type Props = {
    onSuccess: () => void;
    tenantName: string;
    communityName: string;
    logoBucket: string | undefined;
    logoKey: string | undefined;
    openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
};

export const CommunityCreateModal = ({
    onSuccess,
    tenantName,
    communityName,
    logoBucket,
    logoKey,
    openState: [open, setOpen]
}: Props) => {
    const { challengeClient, env } = injectTSDI(Api);
    const [managerUserIds, setManagerUserIds] = useState<string>('');

    const handle = async () => {
        if (tenantName) {
            await challengeClient.support.communitySupportControllerCreate({
                name: communityName,
                type: CommunityType.STANDARD,
                tenantId: tenantName,
                photo: {
                    bucketName:
                        logoBucket ||
                        (env === 'dev'
                            ? 'msr-global-public-assets-dev'
                            : 'msr-global-public-assets-prod'),
                    objectKey:
                        logoKey || 'community-images/default_community.png'
                },
                managerUserIds: managerUserIds
                    .split(',')
                    .map((id) => id.trim()),
                memberInvitation: CommunityAccessLevel.MANAGERS,
                eventCreation: CommunityAccessLevel.MANAGERS,
                tenantMemberAutoJoinEnabled: true,
                leaveEnabled: true,
                initMembers: true,
                notificationsChat:
                    ChatNotificationSelection.MENTIONS_AND_REPLIES,
                locale: LocaleCode.EnUS
            });
            onSuccess();
        }
    };

    return (
        <ConfirmationModal
            open={open}
            submitHandler={handle}
            onModalClose={() => void setOpen(false)}
            buttonText="Create"
        >
            <p className="text-lg font-semibold">
                You are about to create community for tenant <b>{tenantName}</b>
            </p>
            <Alert variant="warning">
                <AlertDescription>
                    Are you sure you want to create community for this tenant?
                </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-1.5">
                <TextField
                    label="Manager User IDs"
                    type="text"
                    value={managerUserIds}
                    onChange={(value) => setManagerUserIds(String(value))}
                />
                <p className="text-xs text-muted-foreground">
                    Enter comma-separated user IDs
                </p>
            </div>
        </ConfirmationModal>
    );
};
